import React, { useState, useEffect } from 'react';
import { useParams, Navigate, NavLink } from 'react-router-dom';
import { Button, Alert, Spinner, Nav, PageHeader } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { areasService, ServiceArea, ForemanArea, ContractArea } from '../../../services/areasService';
import ServiceAreasTable from '../components/ServiceAreasTable';
import ForemanAreasTable from '../components/ForemanAreasTable';
import ContractAreasTable from '../components/ContractAreasTable';
import ServiceAreaModal from '../components/ServiceAreaModal';
import ForemanAreaModal from '../components/ForemanAreaModal';
import ContractAreaModal from '../components/ContractAreaModal';
import AreasFilters from '../components/AreasFilters';
import Toast from '../../../components/Toast';

const AREA_SECTIONS = ['service', 'foreman', 'contract'] as const;
type AreaSection = (typeof AREA_SECTIONS)[number];

interface AreasPageProps {}

const AreasPage: React.FC<AreasPageProps> = () => {
  const { section: sectionParam } = useParams<{ section: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();

  // Data states
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);

  // Modal states
  const [showServiceAreaModal, setShowServiceAreaModal] = useState(false);
  const [showForemanAreaModal, setShowForemanAreaModal] = useState(false);
  const [showContractAreaModal, setShowContractAreaModal] = useState(false);

  // Selected items for editing
  const [selectedServiceArea, setSelectedServiceArea] = useState<ServiceArea | null>(null);
  const [selectedForemanArea, setSelectedForemanArea] = useState<ForemanArea | null>(null);
  const [selectedContractArea, setSelectedContractArea] = useState<ContractArea | null>(null);

  // Filter states
  const [serviceAreaFilter, setServiceAreaFilter] = useState<string>('');
  const [foremanAreaFilter, setForemanAreaFilter] = useState<string>('');

  // Toast state
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Data fetching
  const fetchServiceAreas = async () => {
    try {
      const data = await areasService.getServiceAreas();
      setServiceAreas(data);
    } catch (err) {
      console.error('Error fetching service areas:', err);
      setError('Failed to fetch service areas');
    }
  };

  const fetchForemanAreas = async () => {
    try {
      const data = await areasService.getForemanAreas();
      setForemanAreas(data);
    } catch (err) {
      console.error('Error fetching foreman areas:', err);
      setError('Failed to fetch foreman areas');
    }
  };

  const fetchContractAreas = async () => {
    try {
      const data = await areasService.getAllContractAreas();
      setContractAreas(data);
    } catch (err) {
      console.error('Error fetching contract areas:', err);
      setError('Failed to fetch contract areas');
    }
  };

  /** Load all area lists (same as legacy behavior — needed for tab counts and all tables). */
  const loadAllAreaLists = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchServiceAreas(), fetchForemanAreas(), fetchContractAreas()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sectionParam || !AREA_SECTIONS.includes(sectionParam as AreaSection)) {
      setLoading(false);
      return;
    }
    void loadAllAreaLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch helpers use stable service + setters
  }, [sectionParam]);

  const section =
    sectionParam && AREA_SECTIONS.includes(sectionParam as AreaSection)
      ? (sectionParam as AreaSection)
      : null;

  const activeTab: 'service-areas' | 'foreman-areas' | 'contract-areas' =
    section === 'foreman' ? 'foreman-areas' : section === 'contract' ? 'contract-areas' : 'service-areas';

  // Service Area handlers
  const handleAddServiceArea = () => {
    setSelectedServiceArea(null);
    setShowServiceAreaModal(true);
  };

  const handleEditServiceArea = (serviceArea: ServiceArea) => {
    setSelectedServiceArea(serviceArea);
    setShowServiceAreaModal(true);
  };

  const handleSaveServiceArea = async (serviceAreaData: Partial<ServiceArea>) => {
    try {
      if (selectedServiceArea) {
        await areasService.updateServiceArea(selectedServiceArea.id, serviceAreaData);
        setToastMessage('Service area updated successfully');
      } else {
        await areasService.createServiceArea(serviceAreaData as Omit<ServiceArea, 'id' | 'foremanAreas'>);
        setToastMessage('Service area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowServiceAreaModal(false);
    } catch (err) {
      console.error('Error saving service area:', err);
      setToastMessage('Failed to save service area');
      setToastType('error');
    }
  };

  const handleDeleteServiceArea = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this service area? This will affect all associated foreman areas.')) {
      try {
        await areasService.deleteServiceArea(id);
        setToastMessage('Service area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting service area:', err);
        setToastMessage('Failed to delete service area');
        setToastType('error');
      }
    }
  };

  // Foreman Area handlers
  const handleAddForemanArea = () => {
    setSelectedForemanArea(null);
    setShowForemanAreaModal(true);
  };

  const handleEditForemanArea = (foremanArea: ForemanArea) => {
    setSelectedForemanArea(foremanArea);
    setShowForemanAreaModal(true);
  };

  const handleSaveForemanArea = async (
    foremanAreaData: Partial<ForemanArea> & { contractAreaIds?: number[] }
  ) => {
    try {
      if (selectedForemanArea) {
        await areasService.updateForemanArea(selectedForemanArea.id, foremanAreaData);
        setToastMessage('Foreman area updated successfully');
      } else {
        await areasService.createForemanArea(
          foremanAreaData as Omit<ForemanArea, 'id' | 'serviceArea' | 'contractAreas'> & {
            contractAreaIds?: number[];
          }
        );
        setToastMessage('Foreman area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowForemanAreaModal(false);
    } catch (err) {
      console.error('Error saving foreman area:', err);
      setToastMessage('Failed to save foreman area');
      setToastType('error');
    }
  };

  const handleDeleteForemanArea = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this foreman area? This will affect all associated contract areas.')) {
      try {
        await areasService.deleteForemanArea(id);
        setToastMessage('Foreman area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting foreman area:', err);
        setToastMessage('Failed to delete foreman area');
        setToastType('error');
      }
    }
  };

  // Contract Area handlers
  const handleAddContractArea = () => {
    setSelectedContractArea(null);
    setShowContractAreaModal(true);
  };

  const handleEditContractArea = (contractArea: ContractArea) => {
    setSelectedContractArea(contractArea);
    setShowContractAreaModal(true);
  };

  const handleSaveContractArea = async (contractAreaData: Partial<Omit<ContractArea, 'foremanAreas' | 'assets'>> & { foremanAreaIds?: number[] }) => {
    try {
      if (selectedContractArea) {
        await areasService.updateContractArea(selectedContractArea.id, contractAreaData);
        setToastMessage('Contract area updated successfully');
      } else {
        // Ensure foremanAreaIds is provided for creation
        if (!contractAreaData.foremanAreaIds || contractAreaData.foremanAreaIds.length === 0) {
          throw new Error('At least one foreman area must be selected');
        }
        await areasService.createContractArea(contractAreaData as Omit<ContractArea, 'id' | 'foremanAreas' | 'assets'> & { foremanAreaIds: number[] });
        setToastMessage('Contract area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowContractAreaModal(false);
    } catch (err) {
      console.error('Error saving contract area:', err);
      setToastMessage(err instanceof Error ? err.message : 'Failed to save contract area');
      setToastType('error');
    }
  };

  const handleDeleteContractArea = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this contract area? This will affect all associated assets.')) {
      try {
        await areasService.deleteContractArea(id);
        setToastMessage('Contract area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting contract area:', err);
        setToastMessage('Failed to delete contract area');
        setToastType('error');
      }
    }
  };

  // Filter handlers
  const handleFilterChange = (filters: { serviceArea: string; foremanArea: string }) => {
    setServiceAreaFilter(filters.serviceArea);
    setForemanAreaFilter(filters.foremanArea);
  };

  // Apply filters
  const filteredServiceAreas = serviceAreas.filter(area => 
    area.name?.toLowerCase().includes(serviceAreaFilter.toLowerCase())
  );

  const filteredForemanAreas = foremanAreas.filter(area => {
    const matchesServiceArea = serviceAreaFilter === '' || 
      area.serviceArea?.name?.toLowerCase().includes(serviceAreaFilter.toLowerCase());
    const matchesForemanArea = foremanAreaFilter === '' || 
      area.name?.toLowerCase().includes(foremanAreaFilter.toLowerCase());
    return matchesServiceArea && matchesForemanArea;
  });

  const filteredContractAreas = contractAreas.filter(area => {
    const matchesServiceArea = serviceAreaFilter === '' || 
      area.foremanAreas?.some(fa => fa.serviceArea?.name?.toLowerCase().includes(serviceAreaFilter.toLowerCase()));
    const matchesForemanArea = foremanAreaFilter === '' || 
      area.foremanAreas?.some(fa => fa.name?.toLowerCase().includes(foremanAreaFilter.toLowerCase()));
    return matchesServiceArea && matchesForemanArea;
  });

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="tw-sr-only">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!section) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'service' && !hasPermission('area:service_view')) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'foreman' && !hasPermission('area:foreman_view')) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'contract' && !hasPermission('area:contract_view')) {
    return <Navigate to="/areas" replace />;
  }

  return (
    <div
      className="areas-page tw-p-4"
      style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
    >
      {/* One create action, resolved by the active tab. */}
      <PageHeader
        title="Areas Management"
        actions={[
          {
            key: 'add-service',
            label: 'Add Service Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'service-areas' && hasPermission('area:service_create')),
            onClick: handleAddServiceArea,
          },
          {
            key: 'add-foreman',
            label: 'Add Foreman Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'foreman-areas' && hasPermission('area:foreman_create')),
            onClick: handleAddForemanArea,
          },
          {
            key: 'add-contract',
            label: 'Add Contract Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'contract-areas' && hasPermission('area:contract_create')),
            onClick: handleAddContractArea,
          },
        ]}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AreasFilters
        serviceAreas={serviceAreas}
        foremanAreas={foremanAreas}
        onFilterChange={handleFilterChange}
      />

      {(hasPermission('area:service_view') ||
        hasPermission('area:foreman_view') ||
        hasPermission('area:contract_view')) && (
        <Nav variant="tabs" className="tw-mb-3 areas-page-section-nav border-secondary">
          {hasPermission('area:service_view') && (
            <Nav.Item>
              <Nav.Link as={NavLink} to="/areas/service">
                Service Areas ({serviceAreas.length})
              </Nav.Link>
            </Nav.Item>
          )}
          {hasPermission('area:foreman_view') && (
            <Nav.Item>
              <Nav.Link as={NavLink} to="/areas/foreman">
                Foreman Areas ({foremanAreas.length})
              </Nav.Link>
            </Nav.Item>
          )}
          {hasPermission('area:contract_view') && (
            <Nav.Item>
              <Nav.Link as={NavLink} to="/areas/contract">
                Contract Areas ({contractAreas.length})
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      )}

      {activeTab === 'service-areas' && (
        <ServiceAreasTable
          serviceAreas={filteredServiceAreas}
          onEdit={handleEditServiceArea}
          onDelete={handleDeleteServiceArea}
          canEdit={hasPermission('area:service_edit')}
          canDelete={hasPermission('area:service_delete')}
        />
      )}
      {activeTab === 'foreman-areas' && (
        <ForemanAreasTable
          foremanAreas={filteredForemanAreas}
          onEdit={handleEditForemanArea}
          onDelete={handleDeleteForemanArea}
          canEdit={hasPermission('area:foreman_edit')}
          canDelete={hasPermission('area:foreman_delete')}
        />
      )}
      {activeTab === 'contract-areas' && (
        <ContractAreasTable
          contractAreas={filteredContractAreas}
          onEdit={handleEditContractArea}
          onDelete={handleDeleteContractArea}
          canEdit={hasPermission('area:contract_edit')}
          canDelete={hasPermission('area:contract_delete')}
        />
      )}

      {/* Modals */}
      <ServiceAreaModal
        show={showServiceAreaModal}
        onHide={() => setShowServiceAreaModal(false)}
        serviceArea={selectedServiceArea}
        onSave={handleSaveServiceArea}
      />

      <ForemanAreaModal
        show={showForemanAreaModal}
        onHide={() => setShowForemanAreaModal(false)}
        foremanArea={selectedForemanArea}
        serviceAreas={serviceAreas}
        onSave={handleSaveForemanArea}
      />

      <ContractAreaModal
        show={showContractAreaModal}
        onHide={() => setShowContractAreaModal(false)}
        contractArea={selectedContractArea}
        foremanAreas={foremanAreas}
        onSave={handleSaveContractArea}
      />

      {/* Toast notifications */}
      <Toast
        message={toastMessage}
        type={toastType}
        show={!!toastMessage}
        onClose={() => setToastMessage('')}
      />
    </div>
  );
};

export default AreasPage;